import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import Header from "../../../../../components/common/header";
import clsx from "clsx";
import { getCDNResizedImageUrl } from "../../../../../utils/get-cdn-resized-image-url";
import { notify } from "../../../../../utils/toast";

const GET_PRODUCT = gql`
  query GetProduct($id: Int!) {
    product(id: $id) {
      id
      name
      price
      discountPrice
      discountRate
      quantity
      description
      saleEndTime
      isSampleMenu
      img
      store {
        id
        menus {
          id
          name
          price
          img
        }
      }
    }
  }
`;

const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($input: UpdateProductInput!) {
    updateProduct(input: $input) {
      id
      name
      price
      discountPrice
      quantity
      description
      saleEndTime
    }
  }
`;

const SalesManualUpdate: React.FC = () => {
  const { id } = useParams();
  const productId = id ? parseInt(id, 10) : null; // 안전한 변환
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [menuName, setMenuName] = useState("");
  const [price, setPrice] = useState<number | string | "">("");
  const [discountRate, setDiscountRate] = useState<number | "">(""); // 할인율 추가
  const [imageFile, setImageFile] = useState<File | null>(null); // 이미지 파일로 받기
  const [isSampleMenu, setIsSampleMenu] = useState<boolean>(false); // 샘플 메뉴 상태 추가

  const discountRates = [30, 40, 50, 60]; // 사용할 할인율 목록

  // 미김펙 만자 불라오기
  const { data: dataProduct } = useQuery(GET_PRODUCT, {
    variables: { id: productId },
    skip: productId === null,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    containerRef.current?.scrollTo(0, 0); // 스크롤 초기화
  }, [containerRef]);

  useEffect(() => {
    if (dataProduct) {
      // 기존 마감팩의 값들을 세팅
      setMenuName(dataProduct.product.name);
      setPrice(dataProduct.product.price);
      setDiscountRate(dataProduct.product.discountRate);
      setImageFile(dataProduct.product.img);
      setIsSampleMenu(dataProduct.product.isSampleMenu);
    }
  }, [dataProduct]);

  // 마감팩 업데이트
  const [updateProduct] = useMutation(UPDATE_PRODUCT);

  const handleComplete = async (e: React.FormEvent) => {
    e.preventDefault();

    if (discountRate === "") {
      alert("할인율을 입력해주세요!");
      return;
    }

    if (!menuName || !price || !imageFile) {
      alert("모든 항목을 입력해주세요.");
      return;
    }

    const input = {
      id: Number(id), // '직접입력임을 알리기 위함'
      name: menuName,
      price: Number(price),
      discountPrice: Number(price) - Number(price) * (discountRate / 100),
      discountRate: Number(discountRate),
      quantity: dataProduct.product.quantity,
      isSampleMenu, // 샘플 메뉴 상태 추가
    };

    // 기존 데이터
    const previousData = dataProduct.product;

    // 변경된 데이터 저장
    const changes = [];

    // 1. 이름 변경 확인
    if (previousData.name !== input.name) {
      changes.push(`[이름] ${previousData.name} -> ${input.name}`);
    }

    // 2. 가격 변경 확인
    if (previousData.price !== input.price) {
      changes.push(`[가격] ${previousData.price}원 -> ${input.price}원`);
    }

    // 3. 할인율 변경 확인
    if (previousData.discountRate !== input.discountRate) {
      changes.push(
        `[할인율] ${previousData.discountRate}% -> ${input.discountRate}%`
      );
    }

    // 4. 할인된 가격 변경 확인
    if (previousData.discountPrice !== input.discountPrice) {
      changes.push(
        `[할인가] ${previousData.discountPrice}원 -> ${input.discountPrice}원`
      );
    }

    const isConfirmed = window.confirm(
      `수정 사항을 저장할까요?\n\n${changes.join("\n")}`
    );

    if (!isConfirmed) {
      return;
    }

    await updateProduct({
      variables: {
        input,
      },
    });

    navigate("/boss/live-order/sales"); // 팩 생성 페이지로 이동
    notify("수정되었습니다.");
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="min-h-screen bg-white">
        <Header title="직접 팩 구성하기" showBackButton />
        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-6">팩 직접 수정</h2>
          <form className="space-y-4">
            {/* 팩 이름 */}
            <div>
              <label
                htmlFor="menuName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                팩 이름
              </label>
              <input
                type="text"
                id="menuName"
                value={menuName}
                onChange={(e) => setMenuName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-[#1562fc] focus:border-[#1562fc]"
              />
            </div>

            {/* 이미지 업로드 (파일 입력 + 카메라 + 갤러리 선택 가능) */}
            <div>
              <label
                htmlFor="imageUrl"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                이미지 업로드
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-[#1562fc] focus:border-[#1562fc]">
                <img
                  src={getCDNResizedImageUrl(dataProduct?.product.img, {
                    width: 600,
                    quality: 80,
                  })}
                  alt=""
                />
              </div>
            </div>

            {/* 가격 */}
            <div>
              <label
                htmlFor="price"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                가격 (원)
              </label>
              <input
                type="number"
                id="price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-[#1562fc] focus:border-[#1562fc]"
              />
            </div>

            {/* 할인율 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                할인율 (%)
              </label>
              <div className="flex space-x-4">
                {discountRates.map((rate) => (
                  <button
                    key={rate}
                    onClick={() => setDiscountRate(rate)}
                    type="button"
                    className={clsx("px-4 py-2 border rounded-md", {
                      "bg-blue-500 text-white": discountRate === rate,
                      "bg-gray-200": discountRate !== rate,
                    })}
                  >
                    {rate}%
                  </button>
                ))}
              </div>
            </div>

            {/* 상품 유형 */}
            <div className="mt-6">
              <label className="block text-lg font-semibold mb-2">
                상품 유형
              </label>
              <div className="text-sm text-gray-500 mb-2">
                소비자에게 표시됩니다! 마감상품이 아닌데 마감상품으로 생각하면
                아쉽잖아요 😁
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!isSampleMenu}
                    onChange={(e) => setIsSampleMenu(!e.target.checked)}
                    className="mr-2"
                  />
                  마감상품
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isSampleMenu}
                    onChange={(e) => setIsSampleMenu(e.target.checked)}
                    className="mr-2"
                  />
                  신상품 (홍보용, 신메뉴 테스트, 프로모션, 맛보기 등)
                </label>
              </div>
            </div>

            {/* 완료 버튼 */}
            <div>
              <button
                onClick={handleComplete}
                className="w-full py-3 bg-[#1562fc] text-white rounded-md hover:bg-[#124ab7] transition duration-150"
              >
                완료
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SalesManualUpdate;
